import React, { useState, useEffect, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';

const FORCE_DELAY_MS = 10 * 60 * 1000; // 10 minutes

/**
 * Compact lock banner rendered inside the sidebar when the selected person
 * is locked by another user.
 *
 * Props:
 *   lockInfo     { lockedByName }  — info about the lock holder
 *   requestStatus  'pending' | 'granted' | 'denied' | undefined
 *   onRequest()  — callback to send an access request
 *   onForceAcquire() — callback to force-take the lock
 */
export default function SidebarLockBanner({
  lockInfo,
  requestStatus,
  onRequest,
  onCancelRequest,
  onForceAcquire
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Show Force Take only after waiting 10 minutes with no response
  const [forceAllowed, setForceAllowed] = useState(false);
  const forceTimerRef = useRef(null);

  useEffect(() => {
    if (requestStatus === 'pending') {
      setForceAllowed(false);
      forceTimerRef.current = setTimeout(
        () => setForceAllowed(true),
        FORCE_DELAY_MS
      );
    } else {
      setForceAllowed(false);
      clearTimeout(forceTimerRef.current);
    }
    return () => clearTimeout(forceTimerRef.current);
  }, [requestStatus]);

  if (!lockInfo) return null;

  return (
    <>
      <Alert
        severity="warning"
        icon={<LockIcon fontSize="inherit" />}
        sx={{ mx: 1, my: 1, '& .MuiAlert-message': { width: '100%' } }}
      >
        <Typography
          variant="body2"
          gutterBottom
          sx={{
            fontWeight: 600
          }}
        >
          {lockInfo.lockedByName} is currently editing this record.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            mt: 0.5
          }}
        >
          <Button
            size="small"
            variant="outlined"
            color="warning"
            disabled={requestStatus === 'pending'}
            onClick={onRequest}
            startIcon={
              requestStatus === 'pending' ? (
                <CircularProgress size={12} color="inherit" />
              ) : null
            }
          >
            {requestStatus === 'pending' ? 'Requesting...' : 'Request Access'}
          </Button>
          {requestStatus === 'pending' && (
            <Button
              size="small"
              variant="text"
              color="warning"
              onClick={onCancelRequest}
            >
              Cancel
            </Button>
          )}
          {forceAllowed && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => setConfirmOpen(true)}
            >
              Force Take
            </Button>
          )}
        </Box>
        {requestStatus === 'denied' && (
          <Typography
            variant="caption"
            color="error"
            sx={{
              display: 'block',
              mt: 0.5
            }}
          >
            Your request was declined.
          </Typography>
        )}
      </Alert>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Force take edit access?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will immediately remove{' '}
            <strong>{lockInfo.lockedByName}</strong>
            {`'s lock. Any unsaved changes they have may be lost.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmOpen(false);
              onForceAcquire();
            }}
          >
            Force Take
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
